import {ChevronDownIcon, ChevronUpIcon} from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Center,
    Collapse,
    DarkMode,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    HStack,
    Heading,
    Icon,
    Image,
    Link,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    Spinner,
    Stack,
    Switch,
    Text,
    Tooltip,
    VStack,
    useColorMode,
    useToken,
} from '@chakra-ui/react';
import {useAdminProfile, usePlatformBasicInfo, useProfile} from 'hooks/datahook/auth';
import {usePlatformProduct, useUserPreference} from 'hooks/datahook/settings';
import {useAuth} from 'hooks/useAuth';
import {useColorModeValueKey, useReadableTextColor} from 'hooks/useColors';
import {find, findIndex, get, isNil, set, trim} from 'lodash';
import React, {memo, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {matchPath, useMatches, useNavigate} from 'react-router-dom';
import {
    authPaths,
    navDrawerAdminConfig,
    navDrawerConfig,
    paths,
} from 'routes/path';
import {useUIStore} from 'stores/uiStore';
import tinycolor from 'tinycolor2';
import {HiMoon, HiSun, HiLogout} from 'react-icons/hi';
import {HiStop} from 'react-icons/hi2';
import CountryIcon from 'components/CountryIcon';
import {HEADER_HEIGHT, designSystemColors} from 'theme';
import {motion} from 'framer-motion';
import {useAdminPermissionSettings} from 'hooks/datahook/adminPermission';
import appConfig from 'config/app';
import { useGetApplyDetail } from 'hooks/datahook/debit';

const MotionText = motion(Text);

// Customize if reuqired
export const AppLogoArea = memo((props) => {
    const [sideBarOpen] = useUIStore((state) => [state.sideBarOpen]);
    return (
        <HStack spacing={2} width={'100%'} height={HEADER_HEIGHT} px={3} py={5}>
            <img
                src={sideBarOpen ? appConfig.logo : appConfig.logoShort}
                style={{
                    height: sideBarOpen ? '80px' : '70px',
                    width: sideBarOpen ? '100%' : '40px',
                    objectFit: 'contain',
                    paddingLeft: sideBarOpen ? 12 : 0,
                    paddingRight: sideBarOpen ? 12 : 0,
                    paddingTop: 6,
                    // minWidth: sideBarOpen ? '100%' : '40px',
                }}
            />
        </HStack>
    );
});

export const DrawerItem = memo((props) => {
    const {
        item,
        currentPath,
        onClick,
        isMenu,
        open,
        kycVerified,
        isSubItem,
        collapsed = false,
    } = props;
    const {t} = useTranslation('app');
    const navigate = useNavigate();
    const matches = useMatches();
    const isSelected = !isNil(
        find(matches, (o) => trim(o.pathname, '/') == trim(item.path, '/')),
    );
    const isDisabled = item?.isDisabled || item.kycValid ? (kycVerified ? false : true) : false;
    const disabledColor = useColorModeValueKey('gray');
    const availableColor = useColorModeValueKey('sideBarText');
    const activedColor = useColorModeValueKey('primary');
    const activedTextColor = useReadableTextColor(
        useToken('colors', useColorModeValueKey('primary')),
    );
    // const activedTextColor = 'gray.50'
    const primaryColorValue = useToken('colors', activedColor);
    const naviItemHoverColor = tinycolor(primaryColorValue)
        .setAlpha(0.1)
        .toRgbString();
    const naviItemFocusColor = tinycolor(primaryColorValue)
        .setAlpha(0.2)
        .toRgbString();
    const naviItemActiveColor = tinycolor(primaryColorValue)
        .setAlpha(0.9)
        .toRgbString();
    const MenuIcon = open ? ChevronDownIcon : ChevronUpIcon;
    const [setShowMobileDrawer] = useUIStore((state) => [
        state.setShowMobileDrawer,
    ]);
    return (
        <Box px={3} rounded={'base'} flex={1} opacity={isDisabled ? 0.7 : 1}>
            <Tooltip
                openDelay={300}
                hasArrow
                label={t(item.labelKey)}
                placement="right"
            >
                <HStack
                    px={3}
                    py={2}
                    rounded={'base'}
                    alignItems={'center'}
                    justifyContent={'space-between'}
                    _hover={{
                        bg: isSelected
                            ? 'dark.primary.600'
                            : naviItemHoverColor,
                        _active: {
                            bg: isSelected
                                ? 'dark.primary.700'
                                : naviItemFocusColor,
                        },
                    }}
                    bgColor={isSelected ? primaryColorValue : 'transparent'}
                    cursor={isDisabled ? 'not-allowed' : 'pointer'}
                    onClick={
                        isDisabled
                            ? null
                            : onClick
                                ? onClick
                                : () => {
                                    if (item.path) {
                                        if (item.external) {
                                            window.open(item.path, '_blank');
                                        } else {
                                            navigate(item.path);
                                        }
                                        setShowMobileDrawer(false);
                                    } else if (item.onClick) {
                                        item.onClick();
                                    }
                                }
                    }
                >
                    <HStack alignItems={'center'} w={'100%'} spacing={3}>
                        {isSubItem && !collapsed ?
                            //  item.customIcon  ? ( item.customIcon ) :
                            ( <Box ml={4} /> )
                            : item.customIcon
                                ? ( item.customIcon )
                                : ( item.Icon && (
                                        <Icon
                                            as={item.Icon}
                                            boxSize={3.5}
                                            color={
                                                isSelected
                                                    ? activedTextColor
                                                    : availableColor
                                            }
                                            mb={0.5}
                                        />
                                    )
                                )}
                        <MotionText
                            // color={
                            //     isDisabled
                            //         ? disabledColor
                            //         : isSelected
                            //         ? activedTextColor
                            //         : availableColor
                            // }
                            userSelect="none"
                            fontSize={isSubItem ? 'xs' : 'xs'}
                            fontWeight={isSubItem ? '300' : '400'}
                            noOfLines={1}
                            isTruncated={true}
                            animate={{
                                opacity: collapsed && !isSubItem ? 0 : 1,
                            }}
                            initial={false}
                        >
                            <Text
                                textOverflow='ellipsis'
                                overflow= 'hidden'
                                w={'100%'}
                                fontSize={'14px'}
                                color={
                                    isDisabled
                                        ? disabledColor
                                        : isSelected
                                            ? activedTextColor
                                            : availableColor
                                }
                            >{t(item.labelKey)}</Text>
                        </MotionText>
                        {item.new && !collapsed && (
                            <Badge
                                fontWeight={'400'}
                                variant={'outline'}
                                colorScheme="linkedin"
                            >
                                {t('new')}
                            </Badge>
                        )}
                    </HStack>
                    {isMenu && !collapsed && (
                        <MenuIcon
                            boxSize={5}
                            color={
                                isDisabled
                                    ? disabledColor
                                    : isSelected
                                        ? activedTextColor
                                        : availableColor
                            }
                        />
                    )}
                </HStack>
            </Tooltip>
        </Box>
    );
});

export const DrawerMenuItem = memo((props) => {
    const {item, currentPath, kycVerified, collapsed = false} = props;
    // console.log(currentPath);
    const matches = useMatches();
    const isSelected = !isNil(find(matches, (o) => o.pathname === item.path));
    const [open, setOpen] = useState(isSelected);
    if (collapsed) {
        return (
            <Popover placement="right-start">
                {({isOpen, onClose}) => (
                    <>
                        <PopoverTrigger>
                            <Box>
                                <DrawerItem
                                    currentPath={currentPath}
                                    item={item}
                                    isMenu
                                    onClick={() => {}}
                                    open={isOpen}
                                    kycVerified={kycVerified}
                                    collapsed={collapsed}
                                />
                            </Box>
                        </PopoverTrigger>
                        <Portal>
                            <PopoverContent w={'200px'} placement="right-start">
                                <PopoverArrow />
                                <Stack spacing={2} py={2}>
                                    {item.paths
                                        .filter(
                                            (o) => o.hasPermission !== false,
                                        )
                                        .map((item) => {
                                            return (
                                                <DrawerItem
                                                    key={item.value}
                                                    item={item}
                                                    currentPath={currentPath}
                                                    kycVerified={kycVerified}
                                                    isSubItem
                                                    collapsed={collapsed}
                                                />
                                            );
                                        })}
                                </Stack>
                            </PopoverContent>
                        </Portal>
                    </>
                )}
            </Popover>
        );
    }
    return (
        <Stack spacing={1}>
            <DrawerItem
                currentPath={currentPath}
                item={item}
                onClick={() => setOpen((c) => !c)}
                isMenu
                open={open}
                kycVerified={kycVerified}
                collapsed={collapsed}
            />
            <Collapse in={open} animateOpacity>
                <Stack spacing={1}>
                    {item.paths
                        .filter((o) => o.hasPermission !== false)
                        .map((item) => {
                            return (
                                <DrawerItem
                                    key={item.value}
                                    item={item}
                                    currentPath={currentPath}
                                    kycVerified={kycVerified}
                                    isSubItem
                                    collapsed={collapsed}
                                />
                            );
                        })}
                </Stack>
            </Collapse>
        </Stack>
    );
});

export const MobileNavDrawer = (props) => {
    const [showMobileDrawer, setShowMobileDrawer] = useUIStore((state) => [
        state.showMobileDrawer,
        state.setShowMobileDrawer,
    ]);
    const {data:platformBasicInfo} = usePlatformBasicInfo()
    const bgColor = useColorModeValueKey('sideBarBg');
    const onClose = () => setShowMobileDrawer(false);
    return (
        <Drawer
            isOpen={showMobileDrawer}
            placement="left"
            onClose={onClose}
            size={'full'}
        >
            <DrawerOverlay />
            <DrawerContent
                px={0}
                bg={bgColor}
                transition={'background-color 200ms linear'}
            >
                <DrawerCloseButton color={useColorModeValueKey('text')} />
                <DrawerHeader>
                    <Text color={useColorModeValueKey('text')}>
                        {get(platformBasicInfo, 'companyShortName')}
                    </Text>
                </DrawerHeader>
                <DrawerBody px={0}>
                    <NavDrawer isInMobile />
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

export const NavDrawer = memo(({isInMobile = false}) => {
    const [sideBarOpen] = useUIStore((state) => [state.sideBarOpen]);
    const matches = useMatches();
    const currentPath = get(matches, '[1].pathname');
    const {isUser, roles, isReadOnlyAdmin} = useAuth();
    const {isCompany, kycRequired, kycPending, kycVerified, isIB, id} = (
        isUser ? useProfile : useAdminProfile
    )();
    const {stockPermissions} = usePlatformProduct();
    const {data: basicInfo} = usePlatformBasicInfo();
    const {data: setting} = useUserPreference();
    const {colorMode, toggleColorMode} = useColorMode();
    const {t, i18n} = useTranslation('app');
    const {getPermission} = useAdminPermissionSettings({
        roles,
    });
    const [colorTheme,setColorTheme]= useUIStore((state) => [
        state.colorTheme,
        state.setColorTheme,
    ]);
    const languageList = [
        {
            label: 'englishConsist',
            value: 'en-US',
            county: 'US',
        },
        {
            label: 'chineseConsist',
            value: 'zh-CN',
            county: 'CN',
        },
        {
            label: 'chineseTWConsist',
            value: 'zh-TW',
            county: 'CN',
        },
        {
            label: 'koreanConsist',
            value: 'ko-KR',
            county: 'kr',
        },
    ];
    const themeList = [
        {
            label: t("spaceGray"),
            value: "spaceGray",
            colorHex: "#3e4d66",
        },
        {
            label: t("blue"),
            value: "blue",
            colorHex: "#3c51b9",
        },
        {
            label: t("golden"),
            value: "golden",
            colorHex: "#83644b",
        },
    ];
    const matched = find(languageList, (o) => o.value === i18n.language);
    const themeMatched = find(themeList, (o) => o.value === colorTheme);
    const config = [
        ...(isUser
            ? navDrawerConfig({
                isIB,
                allowDevelop: Boolean(get(setting, 'allowDevelop')),
                isCompany,
                platformBasicInfo: basicInfo,
            })
            : navDrawerAdminConfig({
                getPermission,
                stockPermissions,
                isReadOnlyAdmin
            })),
        ...(isInMobile
            ? [
                {
                    labelKey: colorMode === 'light' ? 'dayMode' : 'nightMode',
                    value: 'darkMode',
                    onClick: toggleColorMode,
                    Icon: colorMode === 'light' ? HiMoon : HiSun,
                },
                {
                    labelKey: 'language',
                    value: 'language',
                    isMenu: true,
                    customIcon: (
                        <CountryIcon country={matched.county} size={16} />
                    ),
                    subMenu: true,
                    paths: languageList.map((item) => ({
                        labelKey: item.label,
                        value: item.value,
                        onClick: () => i18n.changeLanguage(item.value),
                    })),
                },
                {
                    labelKey: 'theme',
                    value: 'theme',
                    isMenu: true,
                    customIcon: (
                        <Icon as={HiStop} color={themeMatched.colorHex} boxSize={5} />
                    ),
                    subMenu: true,
                    paths: themeList.map((item) => ({
                        labelKey: item.label,
                        value: item.value,
                        customIcon: (
                            <Icon as={HiStop} color={item.colorHex} boxSize={5} />
                        ),
                        onClick:() => setColorTheme(item.value)
                    })),
                },
                {
                    labelKey: 'logout',
                    value: 'logout',
                    path: authPaths.logout,
                    Icon: HiLogout,
                    kycValid: false,
                },
            ]
            : []),
    ];
    // console.log(config);
    return (
        <Stack position={'relative'} overflow={'auto'}>
            {false ? (
                <Center>
                    <Spinner color={useColorModeValueKey('gray')} />
                </Center>
            ) : (
                <Stack px={0} spacing={1} position="relative">
                    {config
                        .filter((o) => o.hasPermission !== false)
                        .map((item) => {
                            if (item.subMenu) {
                                return (
                                    <DrawerMenuItem
                                        key={item.value}
                                        item={item}
                                        currentPath={currentPath}
                                        kycVerified={kycVerified}
                                        collapsed={
                                            isInMobile ? false : !sideBarOpen
                                        }
                                    />
                                );
                            } else {
                                return (
                                    <DrawerItem
                                        key={item.value}
                                        item={item}
                                        currentPath={currentPath}
                                        kycVerified={kycVerified}
                                        collapsed={
                                            isInMobile ? false : !sideBarOpen
                                        }
                                    />
                                );
                            }
                        })}
                </Stack>
            )}
            {process.env.REACT_APP_DEV_ENV && sideBarOpen && (
                <Stack mt={12} px={6}>
                    <Text color={'white'}>
                        Build ver: {process.env.REACT_APP_BUILD_VERSION}
                    </Text>
                </Stack>
            )}
        </Stack>
    );
});
